import {
  CoinIcon,
  HammerIcon,
  PackageIcon,
  ShowerIcon,
  WrenchIcon,
} from "@phosphor-icons/react"
import {
  Discount,
  PriceUtils,
  Receipt,
  ReceiptPackage,
  ReceiptPartAction,
  ReceiptProduct,
  ServicePartActionUtils,
  VatRate,
  hasDiscount,
} from "@repo/common"
import { BudgetIndicator } from "@repo/ui/components/budget-indicator"
import {
  ActionComment,
  CollapsibleItem,
  ProductList,
  ReceiptTotals,
  SimpleItem,
} from "@repo/ui/components/receipt-components"
import { Separator } from "@repo/ui/components/ui/separator"
import { Body } from "@repo/ui/components/ui/typography"
import { cn } from "@repo/ui/lib/utils"
import {
  SelectableCardDescription,
  SelectableCardLabel,
} from "@repo/ui/tanstack-form"
import { CommonProps } from "@repo/ui/types/common-props"
import { Check, Plus } from "lucide-react"
import { useState } from "react"
import { useTranslations } from "use-intl"
import { DISCOUNTED_PRICE_CLASS, OriginalPrice } from "./original-price"

/**
 * Renders a "25 % moms (reason)" row. Returns null for items with the default
 * 12% VAT — only the exceptional 25% rate is surfaced to the user.
 */
const Vat25Subtitle = ({
  vatRate,
  reason,
}: {
  vatRate: VatRate | undefined
  reason: string | undefined
}) => {
  const t = useTranslations("ui.receipt")
  if (vatRate !== 25) return null
  return (
    <span className="text-xs text-destructive-foreground">
      {reason ? t("vat25WithReason", { reason }) : t("vat25")}
    </span>
  )
}

interface Props extends CommonProps {
  title?: string
  receipt: Receipt
  proposedItems?: Receipt
  sentItems?: Receipt
  proposedItemsLabel?: string
  jobId?: string
  onAddProduct?: (
    targetType: "package" | "partAction" | "manualAction",
    targetId: string,
    packagePartId?: string,
  ) => void
  onDeleteProduct?: (productId: string) => void
  showBudget?: boolean
  showTotal?: boolean
  /** Show the line between the "Åtgärd" header and the first item. */
  showHeaderSeparator?: boolean
  showVatRates?: boolean
  /**
   * Render an EAN badge next to each product with an `ean`. CRM only — the
   * customer-facing marketplace receipt leaves this off.
   */
  showEan?: boolean
  /** Part IDs whose existing action will be replaced by a proposed action */
  replacedPartIds?: Set<string>
  /**
   * Per-job overrides moving standalone part actions into a package. Renders
   * those actions in a separate "Temporära åtgärder" group at the top of the
   * package, with a divider before the package's regular actions.
   */
  extraPackagePartActions?: { packageId: string; partId: string }[]
  /**
   * Proposed addon part-actions to render nested at the top of an existing
   * package on the work order — with a `+` icon and primary-color label, so
   * the mechanic sees the new action belongs to that package instead of
   * standing alone in the "Nya tillägg" block. Keyed by package
   * `snapshotId`.
   */
  proposedPackagePartActions?: Map<string, ReceiptPartAction[]>
  /**
   * Already-sent addon part-actions to render nested under their package —
   * same treatment as `proposedPackagePartActions` but with the sent (info)
   * styling, so they don't stand alone in the "Redan skickade åtgärder" block.
   * Keyed by package `snapshotId`.
   */
  sentPackagePartActions?: Map<string, ReceiptPartAction[]>
}

export const UnifiedReceipt = ({
  className,
  receipt,
  proposedItems,
  sentItems,
  proposedItemsLabel,
  children,
  onAddProduct,
  onDeleteProduct,
  showBudget = true,
  showTotal = true,
  showHeaderSeparator = true,
  showVatRates = false,
  showEan = false,
  replacedPartIds,
  extraPackagePartActions,
  proposedPackagePartActions,
  sentPackagePartActions,
}: Props) => {
  const t = useTranslations()
  const [collapsedItems, setCollapsedItems] = useState<Record<string, boolean>>(
    {},
  )

  const renderPackageItem = (
    pkg: ReceiptPackage,
    index: number,
    isProposed = false,
    isSent = false,
  ) => {
    const isCollapsed = collapsedItems[pkg.title] ?? true
    const proposedNested = proposedPackagePartActions?.get(pkg.snapshotId) ?? []
    const sentNested = sentPackagePartActions?.get(pkg.snapshotId) ?? []
    const packageDiscount = hasDiscount(pkg.laborPriceDiscount)
      ? pkg.laborPriceDiscount
      : null
    const effectiveLaborPrice = PriceUtils.getPackageLaborPrice(pkg)

    return (
      <CollapsibleItem
        key={`${pkg.title}-${index}`}
        title={pkg.title}
        totalCost={pkg.totalCost}
        products={pkg.products}
        laborPrice={effectiveLaborPrice}
        isCollapsed={isCollapsed}
        onToggleCollapse={() =>
          setCollapsedItems((prev) => ({ ...prev, [pkg.title]: !isCollapsed }))
        }
        isEditable={false}
        showAddButton={!!onAddProduct}
        showDeleteButton={!!onDeleteProduct}
        onAddProduct={
          onAddProduct
            ? () => onAddProduct("package", pkg.snapshotId)
            : undefined
        }
        onDeleteProduct={onDeleteProduct}
        discountInfo={
          packageDiscount
            ? {
                // Included material isn't a discount, so it comes off the
                // struck-through price as well.
                originalLaborPrice:
                  PriceUtils.getPackageOriginalLaborPrice(pkg),
                discount: packageDiscount,
              }
            : undefined
        }
        isProposed={isProposed}
        isSent={isSent}
        isAccepted={!isProposed && !isSent && !!proposedItems}
        icon={
          <PackageIcon weight="regular" className="size-5 text-foreground" />
        }
        showEan={showEan}
        subtitle={
          proposedNested.length > 0 || sentNested.length > 0 ? (
            // Addon actions whose part+action fit inside this package's
            // template. Rendered as the subtitle so they stay visible even
            // when the package's "Arbete" section is collapsed — the
            // mechanic should see the addition without having to expand
            // existing work. Labour is free (covered by the package);
            // products still get listed so the mechanic sees what was added
            // and at what price. Proposed actions use the primary color,
            // already-sent ones the info color.
            <div className="flex flex-col">
              {proposedNested.map((pa) => {
                const actionLabel =
                  pa.actionLabel ??
                  t(ServicePartActionUtils.toLabelKey(pa.partAction))
                return (
                  <div
                    key={`proposed-${pa.partId}-${pa.partAction}`}
                    className="flex flex-col"
                  >
                    <div className="flex items-center gap-1.5 py-1">
                      <Plus className="size-4 text-primary shrink-0" />
                      <SelectableCardDescription className="text-primary">
                        {actionLabel} {pa.partName.toLowerCase()}
                      </SelectableCardDescription>
                    </div>
                    <ProductList
                      products={pa.products}
                      showEan={showEan}
                      isProposed
                      dimProposedIndicator
                    />
                  </div>
                )
              })}
              {sentNested.map((pa) => {
                const actionLabel =
                  pa.actionLabel ??
                  t(ServicePartActionUtils.toLabelKey(pa.partAction))
                return (
                  <div
                    key={`sent-${pa.partId}-${pa.partAction}`}
                    className="flex flex-col"
                  >
                    <div className="flex items-center gap-1.5 py-1">
                      <Plus className="size-4 text-info-foreground shrink-0" />
                      <SelectableCardDescription className="text-info-foreground">
                        {actionLabel} {pa.partName.toLowerCase()}
                      </SelectableCardDescription>
                    </div>
                    <ProductList
                      products={pa.products}
                      showEan={showEan}
                      isSent
                    />
                  </div>
                )
              })}
            </div>
          ) : undefined
        }
      >
        {pkg.wash && (
          <div className="flex justify-between items-start py-1 gap-1">
            <div className="flex items-center gap-1.5">
              <ShowerIcon
                weight="regular"
                className="size-4 text-foreground shrink-0"
              />
              <SelectableCardDescription className="text-foreground">
                {pkg.wash.title}
              </SelectableCardDescription>
            </div>
          </div>
        )}

        {(() => {
          const extraPartIds = new Set(
            extraPackagePartActions
              ?.filter((o) => o.packageId === pkg.originalPackageId)
              .map((o) => o.partId) ?? [],
          )
          const extras = pkg.partActions.filter((pa) =>
            extraPartIds.has(pa.partId),
          )
          const originals = pkg.partActions.filter(
            (pa) => !extraPartIds.has(pa.partId),
          )
          const renderPartActionRow = (
            partAction: (typeof pkg.partActions)[number],
            paIndex: number,
            isExtra: boolean,
          ) => {
            const actionLabel =
              partAction.actionLabel ??
              t(ServicePartActionUtils.toLabelKey(partAction.partAction))
            return (
              <div
                key={`${partAction.partId}-${partAction.partAction}-${paIndex}`}
                className="flex justify-between items-start py-1 gap-1"
              >
                <div className="flex items-center gap-1.5">
                  {isExtra ? (
                    <Plus className="size-4 text-foreground shrink-0" />
                  ) : (
                    <WrenchIcon
                      weight="regular"
                      className="size-4 text-foreground shrink-0"
                    />
                  )}
                  <SelectableCardDescription className="text-foreground">
                    {actionLabel} {partAction.partName.toLowerCase()}
                  </SelectableCardDescription>
                </div>
              </div>
            )
          }
          return (
            <>
              {extras.map((pa, i) => renderPartActionRow(pa, i, true))}
              {extras.length > 0 && originals.length > 0 && (
                <Separator className="my-1" />
              )}
              {originals.map((pa, i) => renderPartActionRow(pa, i, false))}
            </>
          )
        })()}
      </CollapsibleItem>
    )
  }

  const renderReceiptItem = (
    title: string,
    cost: number,
    products: ReceiptProduct[],
    index: number,
    targetType: "partAction" | "manualAction",
    targetId: string,
    isProposed = false,
    isAccepted = false,
    laborDiscountInfo?: {
      originalLaborPrice: number
      discount: Discount | null
    },
    vatRate?: VatRate,
    vat25Reason?: string,
    isSent = false,
    comment?: string,
  ) => {
    const isCollapsed = collapsedItems[title] ?? true
    const productsTotal = PriceUtils.getProductsCost(products)
    const laborPrice = cost - productsTotal
    const vatSubtitle = showVatRates ? (
      <Vat25Subtitle vatRate={vatRate} reason={vat25Reason} />
    ) : undefined
    const itemIcon =
      targetType === "manualAction" ? (
        <HammerIcon weight="regular" className="size-5 text-foreground" />
      ) : (
        <WrenchIcon weight="regular" className="size-5 text-foreground" />
      )

    if (products.length === 0) {
      if (laborDiscountInfo && hasDiscount(laborDiscountInfo.discount)) {
        return (
          <div key={`${title}-${index}`} className="border-b pb-1">
            <div className="flex justify-between items-center py-2 gap-2">
              <div className="flex items-center gap-2">
                {isProposed && (
                  <div className="flex justify-center items-center bg-primary rounded-full size-4">
                    <Plus className="size-3 text-primary-foreground" />
                  </div>
                )}
                {isAccepted && (
                  <div className="flex justify-center items-center bg-secondary-foreground rounded-full size-4">
                    <Check className="size-3 text-background" />
                  </div>
                )}
                {isSent && (
                  <div className="flex justify-center items-center bg-info-foreground rounded-full size-4">
                    <Plus className="size-3 text-background" />
                  </div>
                )}
                {itemIcon}
                <SelectableCardDescription
                  className={cn("font-medium", {
                    "text-primary": isProposed,
                    "text-secondary-foreground": isAccepted,
                    "text-info-foreground": isSent,
                  })}
                >
                  {title}
                </SelectableCardDescription>
              </div>
              <div className="flex flex-col items-end">
                <div className="flex items-center gap-2">
                  <OriginalPrice>
                    {PriceUtils.formatWithoutCurrency(
                      laborDiscountInfo.originalLaborPrice,
                    )}
                  </OriginalPrice>
                  <SelectableCardDescription
                    className={cn(
                      "font-medium",
                      {
                        "text-primary": isProposed,
                        "text-secondary-foreground": isAccepted,
                        "text-info-foreground": isSent,
                      },
                      DISCOUNTED_PRICE_CLASS,
                    )}
                  >
                    {PriceUtils.formatWithoutCurrency(cost)}
                  </SelectableCardDescription>
                </div>
                <span className="text-xs text-destructive-foreground">
                  {t("ui.receipt.discountOnLabor", {
                    discount: PriceUtils.formatDiscountLabel(
                      laborDiscountInfo.discount,
                    ),
                  })}
                </span>
              </div>
            </div>
            {vatSubtitle && <div className="pl-[28px] pb-1">{vatSubtitle}</div>}
            {comment && (
              <div className="pl-[28px]">
                <ActionComment comment={comment} />
              </div>
            )}
          </div>
        )
      }

      return (
        <SimpleItem
          key={`${title}-${index}`}
          title={title}
          cost={cost}
          isEditable={false}
          showAddButton={!!onAddProduct}
          onAddProduct={
            onAddProduct ? () => onAddProduct(targetType, targetId) : undefined
          }
          isProposed={isProposed}
          isAccepted={isAccepted}
          isSent={isSent}
          icon={itemIcon}
          subtitle={vatSubtitle}
          comment={comment}
        />
      )
    }

    return (
      <CollapsibleItem
        key={`${title}-${index}`}
        title={title}
        totalCost={cost}
        products={products}
        laborPrice={laborPrice}
        isCollapsed={isCollapsed}
        onToggleCollapse={() =>
          setCollapsedItems((prev) => ({ ...prev, [title]: !isCollapsed }))
        }
        isEditable={false}
        showAddButton={!!onAddProduct}
        showDeleteButton={!!onDeleteProduct}
        onAddProduct={
          onAddProduct ? () => onAddProduct(targetType, targetId) : undefined
        }
        onDeleteProduct={onDeleteProduct}
        discountInfo={laborDiscountInfo}
        isProposed={isProposed}
        isAccepted={isAccepted}
        isSent={isSent}
        icon={itemIcon}
        subtitle={vatSubtitle}
        comment={comment}
        showEan={showEan}
      />
    )
  }

  // Sent part-actions nested under a package are pulled out of
  // `sentItems.partActions`, so the legend/total must also count the nested map
  // — otherwise an order that only added an action to an existing package would
  // drop the "Redan skickade åtgärder" row even though its cost is in the total.
  const hasSentItems =
    !!sentItems &&
    (sentItems.partActions.length > 0 ||
      sentItems.manualActions.length > 0 ||
      sentItems.packages.length > 0 ||
      sentItems.fees.length > 0 ||
      !!sentItems.wash ||
      (sentPackagePartActions?.size ?? 0) > 0)

  return (
    <div className={cn("space-y-1", className)}>
      {children}
      <div className="flex justify-between items-center text-sm font-medium mb-1 gap-1">
        <SelectableCardLabel>{t("ui.common.action")}</SelectableCardLabel>
        <SelectableCardLabel>{t("ui.receipt.priceSek")}</SelectableCardLabel>
      </div>

      {showHeaderSeparator && <Separator />}

      {/* Render packages */}
      {receipt.packages.map((pkg, index) => renderPackageItem(pkg, index))}

      {/* Render part actions */}
      {receipt.partActions.map((partAction, index) => {
        const laborDiscount = hasDiscount(partAction.laborPriceDiscount)
          ? partAction.laborPriceDiscount
          : null
        const actionLabel =
          partAction.actionLabel ??
          t(ServicePartActionUtils.toLabelKey(partAction.partAction))
        const title = `${actionLabel} ${partAction.partName.toLowerCase()}`
        const isReplaced = replacedPartIds?.has(partAction.partId) ?? false

        if (laborDiscount) {
          // Custom rendering for discounted items
          const isCollapsed = collapsedItems[title] ?? true

          if (partAction.products.length === 0) {
            return (
              <div
                key={`partAction-${partAction.partId}-${index}`}
                className={cn("border-b pb-1", {
                  "opacity-50": isReplaced,
                })}
              >
                {isReplaced && (
                  <div className="text-xs text-warning-foreground pt-1">
                    {t("ui.receipt.replacedByNewAction")}
                  </div>
                )}
                <div className="flex justify-between items-center py-2 gap-2">
                  <div className="flex items-center gap-2">
                    {proposedItems && !isReplaced && (
                      <div className="flex justify-center items-center bg-secondary-foreground rounded-full size-4">
                        <Check className="size-3 text-background" />
                      </div>
                    )}
                    <WrenchIcon
                      weight="regular"
                      className="size-5 text-foreground"
                    />
                    <SelectableCardDescription
                      className={cn("font-medium", {
                        "line-through": isReplaced,
                      })}
                    >
                      {title}
                    </SelectableCardDescription>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="flex items-center gap-2">
                      <OriginalPrice>
                        {PriceUtils.formatWithoutCurrency(
                          partAction.laborPriceOriginal,
                        )}
                      </OriginalPrice>
                      <SelectableCardDescription className="font-medium text-destructive-foreground">
                        {PriceUtils.formatWithoutCurrency(partAction.totalCost)}
                      </SelectableCardDescription>
                    </div>
                    <span className="text-xs text-destructive-foreground">
                      {t("ui.receipt.discountOnLabor", {
                        discount: PriceUtils.formatDiscountLabel(laborDiscount),
                      })}
                    </span>
                  </div>
                </div>
                {showVatRates && (
                  <div className="pl-[28px] pb-1">
                    <Vat25Subtitle
                      vatRate={partAction.vatRate}
                      reason={partAction.vat25Reason}
                    />
                  </div>
                )}
                {partAction.comment && (
                  <div className="pl-[28px]">
                    <ActionComment comment={partAction.comment} />
                  </div>
                )}
              </div>
            )
          }

          return (
            <div
              key={`partAction-${partAction.partId}-${index}`}
              className={cn({ "opacity-50": isReplaced })}
            >
              {isReplaced && (
                <div className="text-xs text-warning-foreground pt-1">
                  {t("ui.receipt.replacedByNewAction")}
                </div>
              )}
              <CollapsibleItem
                title={title}
                totalCost={partAction.totalCost}
                products={partAction.products}
                laborPrice={
                  partAction.laborPriceAfterDiscount ?? partAction.laborPrice
                }
                isCollapsed={isCollapsed}
                onToggleCollapse={() =>
                  setCollapsedItems((prev) => ({
                    ...prev,
                    [title]: !isCollapsed,
                  }))
                }
                isEditable={false}
                showAddButton={!!onAddProduct}
                showDeleteButton={!!onDeleteProduct}
                onAddProduct={
                  onAddProduct
                    ? () => onAddProduct("partAction", partAction.partId)
                    : undefined
                }
                onDeleteProduct={onDeleteProduct}
                discountInfo={{
                  originalLaborPrice: partAction.laborPriceOriginal,
                  discount: laborDiscount,
                }}
                isAccepted={!isReplaced && !!proposedItems}
                icon={
                  <WrenchIcon
                    weight="regular"
                    className="size-5 text-foreground"
                  />
                }
                subtitle={
                  showVatRates ? (
                    <Vat25Subtitle
                      vatRate={partAction.vatRate}
                      reason={partAction.vat25Reason}
                    />
                  ) : undefined
                }
                comment={partAction.comment}
                showEan={showEan}
              />
            </div>
          )
        }

        return (
          <div
            key={`partAction-${partAction.partId}-${index}`}
            className={cn({ "opacity-50": isReplaced })}
          >
            {isReplaced && (
              <div className="text-xs text-warning-foreground pt-1">
                {t("ui.receipt.replacedByNewAction")}
              </div>
            )}
            {renderReceiptItem(
              title,
              partAction.totalCost,
              partAction.products,
              receipt.packages.length + index,
              "partAction",
              partAction.partId,
              false,
              !isReplaced && !!proposedItems,
              undefined,
              partAction.vatRate,
              partAction.vat25Reason,
              false,
              partAction.comment,
            )}
          </div>
        )
      })}

      {/* Render manual actions */}
      {receipt.manualActions.map((manualAction, index) => {
        const hasLaborDiscount = hasDiscount(manualAction.laborPriceDiscount)

        return (
          <div key={`manualAction-${manualAction.id}-${index}`}>
            {renderReceiptItem(
              manualAction.title,
              manualAction.totalCost,
              manualAction.products,
              receipt.packages.length + receipt.partActions.length + index,
              "manualAction",
              manualAction.id,
              false,
              !!proposedItems,
              hasLaborDiscount
                ? {
                    originalLaborPrice: manualAction.laborPriceOriginal,
                    discount: manualAction.laborPriceDiscount,
                  }
                : undefined,
              manualAction.vatRate,
              manualAction.vat25Reason,
              false,
              manualAction.comment,
            )}
          </div>
        )
      })}

      {/* Render standalone wash */}
      {receipt.wash && (
        <SimpleItem
          key={`wash-${receipt.wash.snapshotId}`}
          title={receipt.wash.title}
          cost={receipt.wash.price}
          discount={{
            originalCost: receipt.wash.priceOriginal,
            discount: receipt.wash.priceDiscount,
          }}
          isEditable={false}
          isAccepted={!!proposedItems}
          icon={
            <ShowerIcon weight="regular" className="size-5 text-foreground" />
          }
        />
      )}

      {/* Render fees */}
      {receipt.fees.map((fee) => (
        <SimpleItem
          key={`fee-${fee.id}`}
          title={fee.title}
          cost={fee.price}
          discount={{
            originalCost: fee.priceOriginal,
            discount: fee.priceDiscount,
          }}
          // The parking fee explains how long the bike has waited
          subtitle={
            fee.isParkingFee && fee.description ? (
              <span className="text-xs text-muted-foreground">
                {fee.description}
              </span>
            ) : undefined
          }
          isEditable={false}
          isAccepted={!!proposedItems}
          icon={
            <CoinIcon weight="regular" className="size-5 text-foreground" />
          }
        />
      ))}

      {/* Render sent (previously submitted) items */}
      {sentItems?.partActions.map((partAction, index) => {
        const actionLabel =
          partAction.actionLabel ??
          t(ServicePartActionUtils.toLabelKey(partAction.partAction))
        const title = `${actionLabel} ${partAction.partName.toLowerCase()}`

        return (
          <div key={`sent-partAction-${partAction.partId}-${index}`}>
            {renderReceiptItem(
              title,
              partAction.totalCost,
              partAction.products,
              index,
              "partAction",
              partAction.partId,
              false,
              false,
              undefined,
              undefined,
              undefined,
              true,
            )}
          </div>
        )
      })}

      {sentItems?.manualActions.map((manualAction, index) => (
        <div key={`sent-manualAction-${manualAction.id}-${index}`}>
          {renderReceiptItem(
            manualAction.title,
            manualAction.totalCost,
            manualAction.products,
            index,
            "manualAction",
            manualAction.id,
            false,
            false,
            undefined,
            undefined,
            undefined,
            true,
          )}
        </div>
      ))}

      {sentItems?.wash && (
        <SimpleItem
          key={`sent-wash-${sentItems.wash.snapshotId}`}
          title={sentItems.wash.title}
          cost={sentItems.wash.price}
          discount={{
            originalCost: sentItems.wash.priceOriginal,
            discount: sentItems.wash.priceDiscount,
          }}
          isEditable={false}
          isSent
          icon={
            <ShowerIcon weight="regular" className="size-5 text-foreground" />
          }
        />
      )}

      {sentItems?.fees.map((fee) => (
        <SimpleItem
          key={`sent-fee-${fee.id}`}
          title={fee.title}
          cost={fee.price}
          isEditable={false}
          isSent
          icon={
            <CoinIcon weight="regular" className="size-5 text-foreground" />
          }
        />
      ))}

      {sentItems?.packages.map((pkg, index) =>
        renderPackageItem(pkg, receipt.packages.length + index, false, true),
      )}

      {/* Render proposed packages inline */}
      {proposedItems?.packages.map((pkg, index) =>
        renderPackageItem(pkg, receipt.packages.length + index, true),
      )}

      {/* Render proposed part actions inline */}
      {proposedItems?.partActions.map((partAction, index) => {
        const actionLabel =
          partAction.actionLabel ??
          t(ServicePartActionUtils.toLabelKey(partAction.partAction))
        const title = `${actionLabel} ${partAction.partName.toLowerCase()}`
        const hasLaborDiscount = hasDiscount(partAction.laborPriceDiscount)

        return (
          <div key={`proposed-partAction-${partAction.partId}-${index}`}>
            {renderReceiptItem(
              title,
              partAction.totalCost,
              partAction.products,
              index,
              "partAction",
              partAction.partId,
              true,
              false,
              hasLaborDiscount
                ? {
                    originalLaborPrice: partAction.laborPriceOriginal,
                    discount: partAction.laborPriceDiscount,
                  }
                : undefined,
            )}
          </div>
        )
      })}

      {/* Render proposed manual actions inline */}
      {proposedItems?.manualActions.map((manualAction, index) => {
        const hasLaborDiscount = hasDiscount(manualAction.laborPriceDiscount)

        return (
          <div key={`proposed-manualAction-${manualAction.id}-${index}`}>
            {renderReceiptItem(
              manualAction.title,
              manualAction.totalCost,
              manualAction.products,
              index,
              "manualAction",
              manualAction.id,
              true,
              false,
              hasLaborDiscount
                ? {
                    originalLaborPrice: manualAction.laborPriceOriginal,
                    discount: manualAction.laborPriceDiscount,
                  }
                : undefined,
            )}
          </div>
        )
      })}

      {/* Render proposed wash inline */}
      {proposedItems?.wash && (
        <SimpleItem
          key={`proposed-wash-${proposedItems.wash.snapshotId}`}
          title={proposedItems.wash.title}
          cost={proposedItems.wash.price}
          discount={{
            originalCost: proposedItems.wash.priceOriginal,
            discount: proposedItems.wash.priceDiscount,
          }}
          isEditable={false}
          isProposed
          icon={
            <ShowerIcon weight="regular" className="size-5 text-foreground" />
          }
        />
      )}

      {/* Render proposed fees inline */}
      {proposedItems?.fees.map((fee) => (
        <SimpleItem
          key={`proposed-fee-${fee.id}`}
          title={fee.title}
          cost={fee.price}
          isEditable={false}
          isProposed
          icon={
            <CoinIcon weight="regular" className="size-5 text-foreground" />
          }
        />
      ))}

      {/* Legend for accepted, proposed, and sent items */}
      {(proposedItems &&
        (proposedItems.partActions.length > 0 ||
          proposedItems.manualActions.length > 0 ||
          proposedItems.packages.length > 0 ||
          proposedItems.fees.length > 0 ||
          proposedItems.wash)) ||
      hasSentItems ? (
        <div className="space-y-2 pt-3">
          {proposedItems &&
            (proposedItems.partActions.length > 0 ||
              proposedItems.manualActions.length > 0 ||
              proposedItems.packages.length > 0 ||
              proposedItems.fees.length > 0 ||
              proposedItems.wash) && (
              <>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex justify-center items-center bg-secondary-foreground rounded-full size-4">
                      <Check className="size-3 text-background" />
                    </div>
                    <SelectableCardDescription className="text-secondary-foreground">
                      {t("ui.receipt.alreadyApprovedActions")}
                    </SelectableCardDescription>
                  </div>
                  <SelectableCardDescription className="text-secondary-foreground">
                    {PriceUtils.formatWithoutCurrency(
                      receipt.totalCostAfterOrderDiscount ?? receipt.totalCost,
                    )}
                  </SelectableCardDescription>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex justify-center items-center bg-primary rounded-full size-4">
                      <Plus className="size-3 text-primary-foreground" />
                    </div>
                    <SelectableCardDescription className="text-primary">
                      {proposedItemsLabel ?? t("ui.receipt.proposedItems")}
                    </SelectableCardDescription>
                  </div>
                  <SelectableCardDescription className="text-primary">
                    {PriceUtils.formatWithoutCurrency(proposedItems.totalCost)}
                  </SelectableCardDescription>
                </div>
              </>
            )}
          {sentItems && hasSentItems && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="flex justify-center items-center bg-info-foreground rounded-full size-4">
                  <Plus className="size-3 text-background" />
                </div>
                <SelectableCardDescription className="text-info-foreground">
                  {t("ui.receipt.alreadySentActions")}
                </SelectableCardDescription>
              </div>
              <SelectableCardDescription className="text-info-foreground">
                {PriceUtils.formatWithoutCurrency(sentItems.totalCost)}
              </SelectableCardDescription>
            </div>
          )}
        </div>
      ) : null}

      <div className="space-y-2 pt-3">
        {/* Only show Produkter/Arbete breakdown when there are no proposed items */}
        {!proposedItems &&
          (() => {
            const totalProductsCost =
              PriceUtils.getReceiptTotalProductsCost(receipt)

            const totalLaborPrice =
              receipt.packages.reduce(
                (sum, pkg) => sum + PriceUtils.getPackageLaborPrice(pkg),
                0,
              ) +
              receipt.partActions.reduce(
                (sum, pa) =>
                  sum + (pa.laborPriceAfterDiscount ?? pa.laborPrice),
                0,
              ) +
              receipt.manualActions.reduce(
                (sum, ma) =>
                  sum + (ma.laborPriceAfterDiscount ?? ma.laborPrice),
                0,
              ) +
              (receipt.wash ? receipt.wash.price : 0) +
              receipt.fees.reduce((sum, fee) => sum + fee.price, 0)

            return totalProductsCost > 0 || totalLaborPrice > 0 ? (
              <ReceiptTotals
                totalProductsCost={totalProductsCost}
                totalLaborPrice={totalLaborPrice}
                laborDiscount={receipt.laborDiscount}
                laborCostAfterDiscount={receipt.laborCostAfterDiscount}
                productsDiscount={receipt.productsDiscount}
                productsCostAfterDiscount={receipt.productsCostAfterDiscount}
              />
            ) : null
          })()}

        {showTotal &&
          (proposedItems || sentItems ? (
            <div className="flex flex-col items-end gap-0">
              {proposedItems && (
                <div className="w-full space-y-1 mb-1">
                  <div className="flex justify-between items-start gap-1 w-full">
                    <SelectableCardDescription className="text-secondary-foreground">
                      {t("ui.receipt.previouslyApprovedTotal")}
                    </SelectableCardDescription>
                    <SelectableCardDescription className="text-secondary-foreground">
                      {PriceUtils.formatWithoutCurrency(
                        receipt.totalCostAfterOrderDiscount ??
                          receipt.totalCost,
                      )}
                    </SelectableCardDescription>
                  </div>
                  <div className="flex justify-between items-start gap-1 w-full">
                    <SelectableCardDescription className="text-primary">
                      {t("ui.receipt.addonTotalCost")}
                    </SelectableCardDescription>
                    <SelectableCardDescription className="text-primary">
                      {PriceUtils.formatWithoutCurrency(
                        proposedItems.totalCost,
                      )}
                    </SelectableCardDescription>
                  </div>
                  {sentItems && hasSentItems && (
                    <div className="flex justify-between items-start gap-1 w-full">
                      <SelectableCardDescription className="text-info-foreground">
                        {t("ui.receipt.alreadySentActions")}
                      </SelectableCardDescription>
                      <SelectableCardDescription className="text-info-foreground">
                        {PriceUtils.formatWithoutCurrency(sentItems.totalCost)}
                      </SelectableCardDescription>
                    </div>
                  )}
                </div>
              )}
              <div
                className={cn(
                  "flex justify-between items-start gap-1 w-full",
                  proposedItems && "border-t border-border pt-1",
                )}
              >
                <SelectableCardLabel className="text-foreground">
                  {proposedItems
                    ? t("ui.receipt.newTotalCost")
                    : t("ui.common.total")}
                </SelectableCardLabel>
                <SelectableCardLabel className="text-foreground">
                  {PriceUtils.formatWithoutCurrency(
                    (receipt.totalCostAfterOrderDiscount ?? receipt.totalCost) +
                      (sentItems?.totalCost ?? 0) +
                      (proposedItems?.totalCost ?? 0),
                  )}
                </SelectableCardLabel>
              </div>
              {hasDiscount(receipt.orderDiscount) && (
                <span className="text-xs text-destructive-foreground font-semibold">
                  {t("ui.receipt.orderDiscount", {
                    discount: PriceUtils.formatDiscountLabel(
                      receipt.orderDiscount,
                    ),
                  })}
                </span>
              )}
              {hasDiscount(receipt.laborDiscount) && (
                <span className="text-xs text-destructive-foreground font-semibold">
                  {t("ui.receipt.discountOnLabor", {
                    discount: PriceUtils.formatDiscountLabel(
                      receipt.laborDiscount,
                    ),
                  })}
                </span>
              )}
              {hasDiscount(receipt.productsDiscount) && (
                <span className="text-xs text-destructive-foreground font-semibold">
                  {t("ui.receipt.discountOnProducts", {
                    discount: PriceUtils.formatDiscountLabel(
                      receipt.productsDiscount,
                    ),
                  })}
                </span>
              )}
            </div>
          ) : (
            <div className="flex justify-between items-start gap-1">
              <Body className="font-semibold">{t("ui.common.total")}</Body>
              {hasDiscount(receipt.orderDiscount) ? (
                <div className="flex flex-col items-end">
                  <div className="flex items-center gap-2">
                    <OriginalPrice>
                      {PriceUtils.formatWithoutCurrency(
                        receipt.laborCostAfterDiscount != null ||
                          receipt.productsCostAfterDiscount != null
                          ? (receipt.laborCostAfterDiscount ??
                              receipt.packages.reduce(
                                (sum, pkg) =>
                                  sum + PriceUtils.getPackageLaborPrice(pkg),
                                0,
                              ) +
                                receipt.partActions.reduce(
                                  (sum, pa) =>
                                    sum +
                                    (pa.laborPriceAfterDiscount ??
                                      pa.laborPrice),
                                  0,
                                ) +
                                receipt.manualActions.reduce(
                                  (sum, ma) =>
                                    sum +
                                    (ma.laborPriceAfterDiscount ??
                                      ma.laborPrice),
                                  0,
                                ) +
                                (receipt.wash ? receipt.wash.price : 0) +
                                receipt.fees.reduce(
                                  (sum, fee) => sum + fee.price,
                                  0,
                                )) +
                              (receipt.productsCostAfterDiscount ??
                                receipt.packages.reduce(
                                  (sum, pkg) =>
                                    sum +
                                    pkg.products.reduce(
                                      (s, p) => s + p.price * (p.quantity ?? 1),
                                      0,
                                    ),
                                  0,
                                ) +
                                  receipt.partActions.reduce(
                                    (sum, pa) =>
                                      sum +
                                      pa.products.reduce(
                                        (s, p) =>
                                          s + p.price * (p.quantity ?? 1),
                                        0,
                                      ),
                                    0,
                                  ) +
                                  receipt.manualActions.reduce(
                                    (sum, ma) =>
                                      sum +
                                      ma.products.reduce(
                                        (s, p) =>
                                          s + p.price * (p.quantity ?? 1),
                                        0,
                                      ),
                                    0,
                                  ))
                          : receipt.totalCost,
                      )}
                    </OriginalPrice>
                    <Body className="font-semibold text-destructive-foreground">
                      {PriceUtils.formatWithoutCurrency(
                        receipt.totalCostAfterOrderDiscount ??
                          receipt.totalCost,
                      )}
                    </Body>
                  </div>
                  <span className="text-xs text-destructive-foreground font-semibold">
                    {PriceUtils.formatDiscountLabel(receipt.orderDiscount)}{" "}
                    orderrabatt
                  </span>
                </div>
              ) : receipt.laborDiscount != null ||
                receipt.productsDiscount != null ? (
                <Body className="font-semibold">
                  {PriceUtils.formatWithoutCurrency(
                    receipt.totalCostAfterOrderDiscount ?? receipt.totalCost,
                  )}
                </Body>
              ) : (
                <Body className="font-semibold">
                  {PriceUtils.formatWithoutCurrency(receipt.totalCost)}
                </Body>
              )}
            </div>
          ))}

        {showBudget &&
          !proposedItems &&
          receipt.customerBudget &&
          (() => {
            const effectiveTotal =
              (receipt.totalCostAfterOrderDiscount ?? receipt.totalCost) +
              (sentItems?.totalCost ?? 0)
            return (
              <div className="flex items-center gap-2 pt-2">
                <div
                  className={cn(
                    "min-w-4 min-h-4 rounded-full border border-success-foreground bg-success-background",
                    {
                      "bg-destructive-foreground border-destructive-foreground":
                        effectiveTotal > receipt.customerBudget,
                    },
                  )}
                />
                <Body size="m" weight="semibold">
                  {t("ui.receipt.customerBudget", {
                    budget: PriceUtils.formatWithoutCurrency(
                      receipt.customerBudget,
                    ),
                  })}
                </Body>

                {effectiveTotal > receipt.customerBudget && (
                  <div className="flex items-center gap-2 ml-auto">
                    <div className="min-w-4 min-h-4 rounded-full border bg-destructive-background border-destructive-foreground" />
                    <Body size="m" weight="semibold">
                      {t("ui.receipt.overBudget", {
                        amount: PriceUtils.formatWithoutCurrency(
                          effectiveTotal - receipt.customerBudget,
                        ),
                      })}
                    </Body>
                  </div>
                )}
              </div>
            )
          })()}

        {showBudget && !proposedItems && receipt.customerBudget && (
          <div className="pt-2">
            <BudgetIndicator
              budget={receipt.customerBudget}
              totalCost={
                (receipt.totalCostAfterOrderDiscount ?? receipt.totalCost) +
                (sentItems?.totalCost ?? 0)
              }
            />
          </div>
        )}
      </div>
    </div>
  )
}
